import * as React from 'react';
import styled from '@emotion/styled';
import { Button, Card, Label, Tooltip, Popover, Classes } from '@blueprintjs/core';
import { TLCode } from '../../common/enums';
import {
  bankHoliday,
  bankHolidayDutyOffRequest,
  blackpoint,
  blackpointEconomic,
  blackpointHealthy,
  blackpointSpecial,
  blackpointInactive,
  dutyDay,
  dutyDayNight,
  dutyNight,
  iconE,
  iconModification,
  iconToday,
  help,
} from '../../assets';
import { css } from 'emotion';
import { MultiCalendarSelectionType } from './MultiCalendar';
import { Position } from '@blueprintjs/core/lib/esm/common/position';
import withTranslate, { WithTranslateProps } from './WithTranslate';
import TranslatedMessage from './translation/TranslatedMessage';
import NotificationToast from './NotificationToast';
import { IconNames } from '@blueprintjs/icons';
import moment from 'moment';
import { Instance } from 'mobx-state-tree';
import { SystemStatus } from '../../store/models/SystemStatus';
import { ToolboxToolPart } from './ToolboxParts';

const GreenSpan = styled.span`
  background-color: LightGreen;
`;
const RedSpan = styled.span`
  background-color: Coral;
`;

const HorizontalContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const ToolboxPart = styled.div`
  display: flex;
  flex-direction: row;
`;

export const DivColumn = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 20px;
  line-height: 27spx;
`;

const DivSlimColumn = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 20px;
  height: 20px;
`;

export const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  .bp3-button {
    margin-right: 3px;
  }
`;

const DivLine2 = styled.div`
  display: flex;
  flex-direction: row;
  .bp3-button {
    margin-right: 3px;
  }
`;

const DivColumn2 = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: 20px;
  line-height: 8px;
`;

const NavigateContainer = styled.div`
  display: flex;
  flex-direction: column;
  & .bp3-button {
    margin-bottom: 5px;
  }
`;

const HeaderContainer = styled.div({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
});

export const Title = styled.h2({
  marginRight: '120px',
  marginTop: 0,
  marginBottom: 2,
});

export const imageButtonStyle = css({
  padding: 0,
  marginRight: 10,
});

export interface ToolboxProps extends WithTranslateProps {
  blackPointHolidayCount?: number;
  blackPointHolidayMax?: number;
  blackPointEconomicMax?: number;
  blackPointEncodingStatus?: string;
  annualFreeBankHolidayMax?: number;
  blackPointOnBankHolidayCount?: number;
  annualFreeWeekendMax?: number;
  blackPointOnWeekendCount?: number;
  blackPointEconomicCount?: number;
  isSecretariat?: boolean;
  navigate: (data: any) => void;
  fly?: number;
  sly?: number;
  systemStatus: Array<Instance<typeof SystemStatus>>;
  firstDate: string;
  hideLegend?: boolean;
  onTypeChange: (type: MultiCalendarSelectionType | undefined) => void;
  selectedType?: MultiCalendarSelectionType | undefined;
  hideCounters?: boolean;
  dayDutiesCount?: number;
  dayNightDutiesCount?: number;
  nightDutiesCount?: number;
}

export interface ToolboxState {
  selectedType?: MultiCalendarSelectionType;
  yearFirstLine: number;
  yearSecondLine: number;
  // firstDate: string;
}

class Toolbox extends React.Component<ToolboxProps, ToolboxState> {
  state = {
    yearFirstLine: this.props.fly ? this.props.fly : moment().year(),
    yearSecondLine: this.props.sly ? this.props.sly : moment().year(),
    selectedType: this.props.selectedType ? this.props.selectedType : undefined,
    // firstDate: this.props.systemStatus?(this.props.systemStatus.find((elem) => elem.subSystem === 'NextAcceptableBlackpoint')?moment(this.props.systemStatus.find((elem) => elem.subSystem === 'NextAcceptableBlackpoint')!.status).format("DD/MM/YYYY"):""):""
  } as ToolboxState;

  click = (type: MultiCalendarSelectionType | undefined) => {
    if (type !== this.state.selectedType) {
      this.setState({ selectedType: type });
      this.props.onTypeChange(type);
    }
  };

  componentDidUpdate(prevProps: Readonly<ToolboxProps>, prevState: Readonly<ToolboxState>, snapshot?: any): void {
    const { blackPointHolidayCount, blackPointHolidayMax } = this.props;

    if (
      blackPointHolidayCount != null &&
      blackPointHolidayMax != null &&
      (blackPointHolidayMax - blackPointHolidayCount <= 0 &&
        this.state.selectedType === MultiCalendarSelectionType.blackpoint)
    ) {
      NotificationToast.showInfo(this.props.translate(TLCode.MULTI_CALENDAR_MsgYouHaveReachedQuota));
      this.click(undefined);
    }
    if (this.state.selectedType !== this.props.selectedType) this.setState({ selectedType: this.props.selectedType });
  }

  render() {
    const {
      translate,
      blackPointHolidayCount,
      blackPointHolidayMax,
      blackPointEconomicMax,
      blackPointEncodingStatus,
      annualFreeBankHolidayMax,
      blackPointOnBankHolidayCount,
      annualFreeWeekendMax,
      blackPointOnWeekendCount,
      blackPointEconomicCount,
      isSecretariat,
      navigate,
      fly,
      firstDate,
      hideLegend,
    } = this.props;
    // const { yearFirstLine } = this.state;
    // const { firstDate } = this.state;
    return (
      <>
        <Card
          style={{
            margin: 5,
            padding: 5,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            flex: 1,
          }}
        >
          <HorizontalContainer>
            {!hideLegend && (
              <ToolboxPart>
                <DivColumn>
                  <DivLine>
                    <ToolboxToolPart
                      translate={translate}
                      selectedType={this.state.selectedType}
                      onTypeClicked={this.click}
                      isSecretariat={isSecretariat}
                      blackPointHolidayCount={this.props.blackPointHolidayCount}
                      blackPointHolidayMax={this.props.blackPointHolidayMax}
                    />
                    <DivColumn>
                      <DivLine>
                        <Title>
                          <TranslatedMessage id={TLCode.MULTI_CALENDAR_Legend} />
                        </Title>
                      </DivLine>
                      <DivLine>
                        {!isSecretariat && (
                          <>
                            <Tooltip
                              content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_DutyDay)}
                              position={Position.BOTTOM}
                            >
                              <Button className={imageButtonStyle.toString()} disabled={true}>
                                <img src={dutyDay} />
                              </Button>
                            </Tooltip>
                            <Tooltip
                              content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_DutyDayNight)}
                              position={Position.BOTTOM}
                            >
                              <Button className={imageButtonStyle.toString()} disabled={true}>
                                <img src={dutyDayNight} />
                              </Button>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip
                          content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_DutyNigth)}
                          position={Position.BOTTOM}
                        >
                          <Button className={imageButtonStyle.toString()} disabled={true}>
                            <img src={dutyNight} />
                          </Button>
                        </Tooltip>
                        <Tooltip
                          content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_BankHoliday)}
                          position={Position.BOTTOM}
                        >
                          <Button className={imageButtonStyle.toString()} disabled={true}>
                            <img src={bankHoliday} />
                          </Button>
                        </Tooltip>
                        <Tooltip
                          content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_BankHolidayDutyOffRequest)}
                          position={Position.BOTTOM}
                        >
                          <Button className={imageButtonStyle.toString()} disabled={true}>
                            <img src={bankHolidayDutyOffRequest} />
                          </Button>
                        </Tooltip>
                        <Tooltip
                          content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_Modification)}
                          position={Position.BOTTOM}
                        >
                          <Button className={imageButtonStyle.toString()} disabled={true}>
                            <img src={iconModification} />
                          </Button>
                        </Tooltip>
                        <Tooltip content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_Today)} position={Position.BOTTOM}>
                          <Button className={imageButtonStyle.toString()} disabled={true}>
                            <img src={iconToday} />
                          </Button>
                        </Tooltip>
                      </DivLine>{' '}
                    </DivColumn>
                  </DivLine>
                  <DivLine>
                    <Label>
                      <b>
                        <TranslatedMessage id={TLCode.MULTI_CALENDAR_TOOLBOX_EncodingIsPossible} />
                        &nbsp;<u>{firstDate}</u>
                      </b>
                    </Label>
                  </DivLine>
                </DivColumn>
              </ToolboxPart>
            )}
            {!hideLegend && (
              <ToolboxPart>
                <NavigateContainer>
                  <DivLine>
                    <Tooltip
                      content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_PreviousYear)}
                      position={Position.BOTTOM}
                      hoverOpenDelay={750}
                    >
                      <Button
                        icon={IconNames.CHEVRON_LEFT}
                        large={true}
                        style={{ height: '30px', marginLeft: '5px' }}
                        onClick={() => navigate('UP')}
                      />
                    </Tooltip>
                    <Label
                      style={{
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '25px',
                        verticalAlign: 'center',
                      }}
                    >
                      {fly}
                    </Label>
                    <Tooltip
                      content={translate(TLCode.MULTI_CALENDAR_TOOLBOX_NextYear)}
                      position={Position.BOTTOM}
                      hoverOpenDelay={750}
                    >
                      <Button
                        icon={IconNames.CHEVRON_RIGHT}
                        large={true}
                        style={{ height: '30px', marginLeft: '5px' }}
                        onClick={() => navigate('DOWN')}
                      />
                    </Tooltip>
                  </DivLine>
                  {/* <DivLine>
                <Label>
                  <TranslatedMessage id={TLCode.MULTI_CALENDAR_TOOLBOX_EncodingIsPossible} />
                  &nbsp;{firstDate}
                </Label>
              </DivLine> */}
                </NavigateContainer>
              </ToolboxPart>
            )}
            {this.props.hideCounters && (
              <ToolboxPart>
                <DivColumn>
                  <ToolboxPart />
                </DivColumn>
                <DivColumn2>
                  <ToolboxPart />
                </DivColumn2>
              </ToolboxPart>
            )}
            {this.props.hideLegend &&
              !this.props.hideCounters &&
              blackPointHolidayCount != null &&
              blackPointHolidayMax != null &&
              annualFreeBankHolidayMax != null &&
              blackPointOnBankHolidayCount != null &&
              annualFreeWeekendMax != null &&
              blackPointOnWeekendCount != null &&
              blackPointEconomicMax != null &&
              blackPointEconomicCount != null && (
                <ToolboxPart>
                  <DivSlimColumn>
                    <ToolboxPart>
                      <h4>
                        <TranslatedMessage id={TLCode.MULTI_CALENDAR_TOOLBOX_NrBlackPoint} />
                      </h4>
                      &nbsp;
                      <p>
                        {blackPointHolidayMax} - {blackPointHolidayCount} ={' '}
                        {blackPointHolidayMax - blackPointHolidayCount > 0 ? (
                          <GreenSpan>&nbsp;{blackPointHolidayMax - blackPointHolidayCount}&nbsp;</GreenSpan>
                        ) : (
                          <RedSpan>&nbsp;{blackPointHolidayMax - blackPointHolidayCount}&nbsp;</RedSpan>
                        )}
                      </p>
                      &nbsp;
                      {/* <h4> */}
                      <TranslatedMessage id={TLCode.MULTI_CALENDAR_TOOLBOX_NrFreeBankHolidayBalance} />
                      {/* </h4> */}
                      &nbsp;
                      {blackPointHolidayMax - blackPointHolidayCount > 0 && (
                        <p>
                          {annualFreeBankHolidayMax} - {blackPointOnBankHolidayCount} ={' '}
                          {annualFreeBankHolidayMax - blackPointOnBankHolidayCount}
                        </p>
                      )}
                      {blackPointHolidayMax - blackPointHolidayCount == 0 && <Label>N/A</Label>}
                      ,&nbsp;
                      {/* <h4> */}
                      <TranslatedMessage id={TLCode.MULTI_CALENDAR_TOOLBOX_NrFreeWeekendBalance} />
                      {/* </h4> */}
                      &nbsp;
                      {blackPointHolidayMax - blackPointHolidayCount > 0 && (
                        <p>
                          {annualFreeWeekendMax} - {blackPointOnWeekendCount} ={' '}
                          {annualFreeWeekendMax - blackPointOnWeekendCount}
                        </p>
                      )}
                      {blackPointHolidayMax - blackPointHolidayCount == 0 && <Label>N/A</Label>}
                      {isSecretariat && (
                        <p>
                          ,&nbsp;
                          {/* <h4> */}
                          <TranslatedMessage id={TLCode.MULTI_CALENDAR_TOOLBOX_NrFreeEconomicBlackpoint} />
                          {/* </h4> */}
                          &nbsp;
                          {blackPointEconomicMax - blackPointEconomicCount != 0 && (
                            <span>
                              {blackPointEconomicMax} - {blackPointEconomicCount} ={' '}
                              {blackPointEconomicMax - blackPointEconomicCount}
                            </span>
                          )}
                          {blackPointEconomicMax - blackPointEconomicCount == 0 && <Label>N/A</Label>}
                        </p>
                      )}
                    </ToolboxPart>
                  </DivSlimColumn>
                </ToolboxPart>
              )}
            {!this.props.hideLegend &&
              !this.props.hideCounters &&
              blackPointHolidayCount != null &&
              blackPointHolidayMax != null &&
              annualFreeBankHolidayMax != null &&
              blackPointOnBankHolidayCount != null &&
              annualFreeWeekendMax != null &&
              blackPointOnWeekendCount != null &&
              blackPointEconomicMax != null &&
              blackPointEconomicCount != null && (
                <ToolboxPart>
                  <DivColumn>
                    <p style={{ color: 'red' }}>
                      <u>
                        <b>
                          <TranslatedMessage id={TLCode.MULTI_CALENDAR_TOOLBOX_NrBlackPointInfo} />
                        </b>
                      </u>
                    </p>
                  </DivColumn>
                  <DivColumn>
                    <ToolboxPart>
                      {/* <Popover content={<TranslatedMessage id={TLCode.MULTI_CALENDAR_TOOLBOX_QuotaHelp}></TranslatedMessage>} position={Position.BOTTOM}
                    hoverOpenDelay={150}
                    interactionKind="hover"
                    target={<img src={help} width="16" height="16" color="blue"/>}
                    >
                      
                    </Popover>&nbsp; */}
                      <h4>
                        <TranslatedMessage id={TLCode.MULTI_CALENDAR_TOOLBOX_NrBlackPoint} />
                      </h4>
                      &nbsp;
                      <Label>
                        {blackPointHolidayMax} - {blackPointHolidayCount} ={' '}
                        {blackPointHolidayMax - blackPointHolidayCount > 0 ? (
                          <GreenSpan>&nbsp;{blackPointHolidayMax - blackPointHolidayCount}&nbsp;</GreenSpan>
                        ) : (
                          <RedSpan>&nbsp;{blackPointHolidayMax - blackPointHolidayCount}&nbsp;</RedSpan>
                        )}
                      </Label>
                    </ToolboxPart>
                  </DivColumn>
                  <DivColumn2>
                    <ToolboxPart>
                      {/* <h4> */}
                      <TranslatedMessage id={TLCode.MULTI_CALENDAR_TOOLBOX_NrFreeBankHolidayBalance} />
                      {/* </h4> */}
                      &nbsp;
                      {blackPointHolidayMax - blackPointHolidayCount > 0 && (
                        <Label>
                          {annualFreeBankHolidayMax} - {blackPointOnBankHolidayCount} ={' '}
                          {annualFreeBankHolidayMax - blackPointOnBankHolidayCount}
                        </Label>
                      )}
                      {blackPointHolidayMax - blackPointHolidayCount == 0 && <Label>N/A</Label>}
                    </ToolboxPart>
                    <ToolboxPart>
                      {/* <h4> */}
                      <TranslatedMessage id={TLCode.MULTI_CALENDAR_TOOLBOX_NrFreeWeekendBalance} />
                      {/* </h4> */}
                      &nbsp;
                      {blackPointHolidayMax - blackPointHolidayCount > 0 && (
                        <Label>
                          {annualFreeWeekendMax} - {blackPointOnWeekendCount} ={' '}
                          {annualFreeWeekendMax - blackPointOnWeekendCount}
                        </Label>
                      )}
                      {blackPointHolidayMax - blackPointHolidayCount == 0 && <Label>N/A</Label>}
                    </ToolboxPart>
                    {isSecretariat && (
                      <ToolboxPart>
                        {/* <h4> */}
                        <TranslatedMessage id={TLCode.MULTI_CALENDAR_TOOLBOX_NrFreeEconomicBlackpoint} />
                        {/* </h4> */}
                        &nbsp;
                        {blackPointEconomicMax - blackPointEconomicCount != 0 && (
                          <Label>
                            {blackPointEconomicMax} - {blackPointEconomicCount} ={' '}
                            {blackPointEconomicMax - blackPointEconomicCount}
                          </Label>
                        )}
                        {blackPointEconomicMax - blackPointEconomicCount == 0 && <Label>N/A</Label>}
                      </ToolboxPart>
                    )}
                  </DivColumn2>
                </ToolboxPart>
              )}
          </HorizontalContainer>
        </Card>
        {this.props.hideLegend && !this.props.hideCounters && (
          <Card
            style={{
              margin: 5,
              padding: 5,
            }}
          >
            <HorizontalContainer>
              <DivSlimColumn>
                <ToolboxPart>
                  <b>
                    <Tooltip className={Classes.TOOLTIP_INDICATOR} content="Nbre total de gardes sur le semestre">
                      <TranslatedMessage id={TLCode.Duties} />
                    </Tooltip>
                    &nbsp;
                    {(this.props.dayDutiesCount === undefined ? 0 : this.props.dayDutiesCount) +
                      (this.props.dayNightDutiesCount === undefined ? 0 : this.props.dayNightDutiesCount) +
                      (this.props.nightDutiesCount === undefined ? 0 : this.props.nightDutiesCount)}
                    &nbsp;
                    <Tooltip
                      className={Classes.TOOLTIP_INDICATOR}
                      content="Nbre total de gardes de jour seul sur le semestre"
                    >
                      <TranslatedMessage id={TLCode.Days} />
                    </Tooltip>
                    &nbsp;
                    {this.props.dayDutiesCount === undefined ? 0 : this.props.dayDutiesCount}
                    &nbsp;
                    <Tooltip
                      className={Classes.TOOLTIP_INDICATOR}
                      content="Nbre total de gardes de nuit (avec/sans jour) sur le semestre"
                    >
                      <TranslatedMessage id={TLCode.Nights} />
                    </Tooltip>
                    &nbsp;
                    {(this.props.dayNightDutiesCount === undefined ? 0 : this.props.dayNightDutiesCount) +
                      (this.props.nightDutiesCount === undefined ? 0 : this.props.nightDutiesCount)}
                  </b>
                </ToolboxPart>
              </DivSlimColumn>
            </HorizontalContainer>
          </Card>
        )}
      </>
    );
  }
}

export default withTranslate(Toolbox);
